import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, Spinner } from '@spark';

import { useAuth } from '@/features/auth/AuthContext.jsx';
import WaitlistLayout, { WaitlistLogo } from '@/features/waiting-list/components/WaitlistLayout';

export const NO_ACCESS_TITLE = "You don't have access yet";
export const NO_ACCESS_BODY =
    'This installation is invitation-only. Ask a workspace admin to invite you, then sign in again.';
export const NO_ACCESS_SIGNED_IN_AS = 'Signed in as';

/**
 * "No access" page at `/request-access` for self-hosted installs.
 *
 * `POST /users/bootstrap` sends strangers (state `no_access`) and members
 * who lost their last workspace here when the waiting list is off
 * (`IS_SAAS=false`).  There is no self-service: the only way in is an
 * invitation from a workspace admin, so the page shows who is signed in and
 * offers "Log out", which lands on `/login`.  Signing in after the invitation
 * is consumed takes the person straight to their workspace, so this page
 * never needs a "check again" action.
 *
 * Reuses the waiting-list "done" chrome so both access gates look the same.
 */
export default function NoAccessPage() {
    const navigate = useNavigate();
    const { user, loading, isAuthEnabled, logout } = useAuth();
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (loading) return;
        if (!isAuthEnabled) {
            // Without authentication everyone has access; nothing to gate.
            navigate('/', { replace: true });
            return;
        }
        if (!user) {
            navigate('/login', { replace: true });
        }
    }, [loading, user, isAuthEnabled, navigate]);

    const signOut = useCallback(async () => {
        if (busy) return;
        setBusy(true);
        try {
            await logout();
        } catch (err) {
            // The session cookie may survive, but the login page re-runs bootstrap and parks them here again.
            console.warn('Logout failed', err);
        } finally {
            setBusy(false);
            navigate('/login', { replace: true });
        }
    }, [busy, logout, navigate]);

    if (loading || !user) {
        return (
            <WaitlistLayout variant="done">
                <div className="waitlist-loading">
                    <Spinner size="large" aria-label="Checking your access" />
                </div>
            </WaitlistLayout>
        );
    }

    return (
        <WaitlistLayout variant="done">
            <WaitlistLogo size="small" />
            <h1 className="waitlist-done-title waitlist-inter-light-36">{NO_ACCESS_TITLE}</h1>
            <p className="waitlist-done-body waitlist-inter-light-24">
                {NO_ACCESS_BODY}
                {user.email ? `\n\n${NO_ACCESS_SIGNED_IN_AS} ${user.email}` : ''}
            </p>
            <div className="waitlist-done-footer">
                <Button size="large" variant="primary" disabled={busy} onClick={signOut}>
                    Log out
                </Button>
            </div>
        </WaitlistLayout>
    );
}
